import React, { createContext, useState, useContext, useEffect } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase-config"; // Your initialized Firestore instance

// Create the authentication context
const AuthContext = createContext();

// AuthProvider component to wrap your application
export const AuthProvider = ({ children }) => {
  const [userProfile, setUserProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth();

    // onAuthStateChanged is the single source of truth for the user's session
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is signed in, fetch their profile from Firestore
        const userDocRef = doc(db, "USERS", user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          // Combine the user object from Firebase Auth with your Firestore data
          setUserProfile({
            uid: user.uid,
            displayName: user.displayName,
            email: user.email,
            photoURL: user.photoURL,
            ...userData, // Your custom fields like isAdmin
          });
          setIsAdmin(userData.isAdmin || false);
        } else {
          // This handles cases where a user exists in Firebase Auth but not in your 'USERS' collection.
          // You might want to create the user document here or log an error.
          setUserProfile(user); // Fallback to only Firebase Auth data
          setIsAdmin(false);
        }
      } else {
        // User is signed out
        setUserProfile(null);
        setIsAdmin(false);
      }

      // This is crucial: set loading to false after the auth check is complete,
      // regardless of whether the user is logged in or out.
      setIsLoading(false);
    });

    // Cleanup the subscription when the component unmounts
    return () => unsubscribe();
  }, []); // The empty dependency array ensures this effect runs only once on mount

  // Derive isAuthenticated directly from the userProfile state
  const isAuthenticated = !!userProfile;

  // Provide the auth state to the rest of your app
  const value = {
    isAuthenticated,
    userProfile,
    isAdmin,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to easily consume the authentication context
export const useAuth = () => {
  return useContext(AuthContext);
};
